import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";

// ─────────────────────────────────────────────────────────────────────────
// Owner KPIs — input-vs-output performance scorecard for Dylan & Colton.
// Data comes from /api/coach?action=owner-kpis (see api/coach.js). Every metric
// that can't be truthfully computed carries an honest coverage badge or a
// "not tracked" note — no estimates dressed up as facts.
// ─────────────────────────────────────────────────────────────────────────

const API = "/api/coach";

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
const fmtNum = (n, d = 0) =>
  (Number(n) || 0).toLocaleString("en-CA", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtDate = (s) => {
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
};

const OWNER_KEYS = ["dylan", "colton"];
const OWNER_LABELS = { dylan: "Dylan", colton: "Colton" };

const CAT_COLORS = {
  welder_gen_service: "#a5ce3a",
  gen_install: "#f59e0b",
  residential: "#38bdf8",
  commercial: "#818cf8",
  industrial: "#f472b6",
  solar: "#fbbf24",
  ev: "#34d399",
  other: "#94a3b8",
  unclassified: "#4b5563",
};

export default function OwnerKpis() {
  const navigate = useNavigate();
  const [range, setRange] = useState("month");   // week | month | year
  const [anchor, setAnchor] = useState("");        // ISO date inside the window ("" = today)
  const [owner, setOwner] = useState("both");      // dylan | colton | both
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);  // category drill-down sidebar

  const load = useCallback(async () => {
    setBusy(true); setError("");
    const qs = new URLSearchParams({ action: "owner-kpis", range });
    if (anchor) qs.set("anchor", anchor);
    const url = `${API}?${qs.toString()}`;
    // Retry a few times with backoff — a fetch that throws (vs returns an error
    // JSON) is a network-level blip, usually a mid-deploy function swap. Auto-
    // recover instead of showing "Failed to fetch".
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await apiFetch(url);
        const r = await res.json();
        if (!r.ok) throw new Error(r.error || "Failed to load KPIs");
        setData(r);
        setAi(null);
        setSelectedCat(null);
        setBusy(false);
        return;
      } catch (e) {
        lastErr = e;
        if (attempt < 2) await new Promise((s) => setTimeout(s, 700 * (attempt + 1)));
      }
    }
    setError((lastErr && lastErr.message) || "Failed to load KPIs");
    setBusy(false);
  }, [range, anchor]);

  useEffect(() => { load(); }, [load]);

  const shownOwners = owner === "both" ? OWNER_KEYS : [owner];

  const runAi = async () => {
    if (!data) return;
    setAiBusy(true); setError("");
    try {
      const payload = {
        window: data.window,
        selected_owner: owner,
        owners: Object.fromEntries(shownOwners.map((k) => [k, data.owners[k]])),
        company: data.company,
        categories: data.categories,
        coverage: data.coverage,
        notes: data.notes,
      };
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "owner-kpi-summary", payload }),
      }).then((x) => x.json());
      if (!r.ok) throw new Error(r.error || "AI summary failed");
      setAi(r.summary);
    } catch (e) {
      setError(e.message);
    } finally {
      setAiBusy(false);
    }
  };

  const win = data?.window;
  const cov = data?.coverage;
  const catMax = Math.max(1, ...((data?.categories || []).map((c) => c.revenue)));

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <button onClick={() => navigate("/coach")} style={S.backLink}>‹ Coach</button>
          <div style={S.title}>Owner KPIs</div>
          <div style={S.subtitle}>Input vs output for Dylan &amp; Colton — hours &amp; leads in, revenue &amp; sales out. Real logged data only.</div>
        </div>
      </div>

      {error && <div style={S.errorBanner}>{error}<button style={S.bannerClose} onClick={() => setError("")}>×</button></div>}

      {/* Controls */}
      <div style={S.controls}>
        <div style={S.segmented}>
          {[["week", "Week"], ["month", "Month"], ["year", "Year"]].map(([v, lbl]) => (
            <button key={v} onClick={() => { setRange(v); setAnchor(""); }} style={range === v ? S.segActive : S.seg}>{lbl}</button>
          ))}
        </div>
        <div style={S.navGroup}>
          <button style={S.navBtn} disabled={busy} onClick={() => setAnchor(win?.prevAnchor || "")}>‹</button>
          <span style={S.rangeLabel}>{win?.label || "…"}</span>
          <button style={S.navBtn} disabled={busy} onClick={() => setAnchor(win?.nextAnchor || "")}>›</button>
          {anchor && <button style={S.todayBtn} disabled={busy} onClick={() => setAnchor("")}>Today</button>}
        </div>
        <div style={{ ...S.segmented, marginLeft: "auto" }}>
          {[["both", "Both"], ["dylan", "Dylan"], ["colton", "Colton"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setOwner(v)} style={owner === v ? S.segActive : S.seg}>{lbl}</button>
          ))}
        </div>
      </div>

      {busy && !data && <div style={S.empty}>Loading…</div>}

      {data && (
        <>
          {/* Company strip */}
          <div style={S.companyStrip}>
            <Stat label="Revenue Invoiced" value={fmtMoney(data.company.revenue)} sub={`${data.company.invoices} invoices billed out`} />
            <Stat label="Quotes Sent" value={fmtNum(data.company.quotes.sentCount)} sub={fmtMoney(data.company.quotes.sentValue)} />
            <Stat label="Sales Converted" value={fmtNum(data.company.quotes.convertedCount)} sub={fmtMoney(data.company.quotes.convertedValue)} />
            <Stat label="Requests" value={fmtNum(data.company.requests.total)} sub={`${data.company.requests.completed} completed`} />
          </div>

          {/* Owner scorecards */}
          <div style={{ ...S.ownerGrid, gridTemplateColumns: `repeat(${shownOwners.length}, minmax(0, 1fr))` }}>
            {shownOwners.map((k) => {
              const o = data.owners[k];
              return (
                <div key={k} style={S.ownerCard}>
                  <div style={S.ownerName}>
                    <span style={S.ownerDot} /> {o.name}
                  </div>

                  <div style={S.sectionLabel}>Time in</div>
                  <div style={S.kpiRow}>
                    <Mini label="On jobs" value={fmtNum(o.hours.job, 1)} sub="billable" />
                    <Mini label="Assessments" value={fmtNum(o.hours.request, 1)} sub="request visits" />
                    <Mini label="Office" value={fmtNum(o.hours.office, 1)} sub="general / admin" />
                    <Mini label="Total" value={fmtNum(o.hours.total, 1)} sub="a floor" />
                  </div>

                  <div style={S.sectionLabel}>Revenue out (invoiced this period)</div>
                  <div style={S.kpiRow}>
                    <Mini label="Sold" value={fmtMoney(o.sold)} sub="jobs you're the salesperson on" />
                    <Mini label="Produced" value={fmtMoney(o.produced)} sub="field: welding + your job-hours" />
                  </div>

                  <div style={S.sectionLabel}>Quotes &amp; sales</div>
                  <div style={S.kpiRow}>
                    <Mini label="Quotes sent" value={fmtNum(o.quotes.sentCount)} sub={fmtMoney(o.quotes.sentValue)} />
                    <Mini label="Sales converted" value={fmtNum(o.quotes.convertedCount)} sub={fmtMoney(o.quotes.convertedValue)} />
                    <Mini label="Conversion" value={o.quotes.conversionCohort == null ? "—" : `${o.quotes.conversionCohort}%`} sub="of sent this period" />
                  </div>

                  <div style={S.sectionLabel}>Leads owned</div>
                  <div style={S.kpiRow}>
                    <Mini label="Leads owned" value={fmtNum(o.leads.owned)} sub={`${o.leads.converted} converted`} />
                    <Mini label="Completed" value={fmtNum(o.leads.completed)} sub="assessments done" />
                  </div>

                  {/* Per-owner Produced revenue by category */}
                  {Object.keys(o.producedByCategory || {}).length > 0 && (
                    <>
                      <div style={S.sectionLabel}>Produced by category</div>
                      <div style={{ marginTop: 4 }}>
                        {Object.entries(o.producedByCategory)
                          .sort((a, b) => b[1] - a[1])
                          .map(([c, v]) => {
                            const lbl = (data.categories.find((x) => x.key === c) || {}).label || c;
                            const max = Math.max(1, ...Object.values(o.producedByCategory));
                            return (
                              <button key={c} onClick={() => setSelectedCat(c)} style={S.catRowBtn} title="Click to see invoices">
                                <div style={S.catName}>{lbl}</div>
                                <div style={S.catBarTrack}>
                                  <div style={{ ...S.catBarFill, width: `${(v / max) * 100}%`, background: CAT_COLORS[c] || "#94a3b8" }} />
                                </div>
                                <div style={S.catVal}>{fmtMoney(v)}</div>
                              </button>
                            );
                          })}
                        <div style={S.catTotalRow}>
                          <div style={{ ...S.catName, fontWeight: 700 }}>Total produced</div>
                          <div style={{ flex: 1 }} />
                          <div style={{ ...S.catVal, fontWeight: 800 }}>{fmtMoney(o.produced)}</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Coverage badges */}
          <div style={S.badgeRow}>
            <Badge label="Revenue credited to an owner (Sold)" pct={cov.soldAttributedPct} count={`${fmtMoney(data.company.soldUnassigned)} unassigned`} warnBelow={50} />
            <Badge label="Quotes attributed to an owner" pct={cov.quotesWithOwnerPct} count={`${cov.quotesSentTotal} sent`} />
            <Badge label="Invoices with a known category" pct={cov.categoryClassifiedPct} count={`${cov.unclassifiedPct}% rev unclassified`} warnBelow={50} />
          </div>

          {/* Company revenue by service category */}
          <div style={S.card}>
            <div style={S.cardHead}>
              <div style={S.cardTitle}>Revenue by Service Category — invoiced in {win.label}</div>
              <div style={S.cardSub}>{cov.categoryClassifiedPct}% classified · {fmtMoney(cov.unclassifiedRevenue)} unclassified · click a row for invoices</div>
            </div>
            {data.categories.length === 0 && <div style={S.emptyInline}>No invoices in this window.</div>}
            {data.categories.map((c) => (
              <button key={c.key} onClick={() => setSelectedCat(c.key)} style={S.catRowBtn} title="Click to see invoices">
                <div style={{ ...S.catName, width: 150 }}>{c.label}</div>
                <div style={S.catBarTrack}>
                  <div style={{ ...S.catBarFill, width: `${(c.revenue / catMax) * 100}%`, background: CAT_COLORS[c.key] || "#94a3b8" }} />
                </div>
                <div style={S.catVal}>{fmtMoney(c.revenue)}<span style={S.catPct}> · {c.pct}%</span></div>
                <div style={S.catChevron}>›</div>
              </button>
            ))}
            {data.categories.length > 0 && (
              <div style={S.catTotalRow}>
                <div style={{ ...S.catName, width: 150, fontWeight: 700 }}>Total</div>
                <div style={{ flex: 1 }} />
                <div style={{ ...S.catVal, fontWeight: 800 }}>{fmtMoney(data.company.revenue)}</div>
                <div style={S.catChevron} />
              </div>
            )}
            {cov.unclassifiedPct > 20 && (
              <div style={S.catNote}>
                ⚠ {cov.unclassifiedPct}% of revenue is unclassified — mostly older jobs left on the "Service Type" placeholder in Jobber. Newer jobs auto-fill it, so recent windows read cleaner.
              </div>
            )}
            <div style={S.catHint}>Company totals = full invoice value per category. "Sold" credits the salesperson; "Produced" credits field execution (Welder/Gen Service → Colton in full, other jobs by crew-hours share). Revenue with no salesperson shows as Unassigned in the Sold lens.</div>
          </div>

          {/* AI coaching summary */}
          <div style={S.card}>
            <div style={S.cardHead}>
              <div style={S.cardTitle}>Jai's Read — where to improve</div>
              <button style={S.aiBtn} onClick={runAi} disabled={aiBusy}>{aiBusy ? "Thinking…" : (ai ? "Regenerate" : "Generate summary")}</button>
            </div>
            {!ai && !aiBusy && <div style={S.emptyInline}>Generate an AI read of this period's input-vs-output for {owner === "both" ? "both owners" : OWNER_LABELS[owner]}.</div>}
            {ai && (
              <div>
                <div style={S.aiHeadline}>{ai.headline}</div>
                {(ai.wins || []).length > 0 && (
                  <>
                    <div style={S.aiSubhead}>Wins</div>
                    <ul style={S.aiList}>{ai.wins.map((x, i) => <li key={i} style={S.aiWin}>{x}</li>)}</ul>
                  </>
                )}
                {(ai.focus_areas || []).length > 0 && (
                  <>
                    <div style={S.aiSubhead}>Focus areas</div>
                    <ul style={S.aiList}>{ai.focus_areas.map((x, i) => <li key={i} style={S.aiFocus}>{x}</li>)}</ul>
                  </>
                )}
                {ai.recommendation && <div style={S.aiRec}>{ai.recommendation}</div>}
              </div>
            )}
          </div>

          {/* Honesty notes */}
          <div style={S.notes}>
            {(data.notes || []).map((n, i) => <div key={i} style={S.noteLine}>• {n}</div>)}
          </div>

          {/* Category drill-down sidebar — invoices in this category */}
          {selectedCat && (() => {
            const catLabel = (data.categories.find((c) => c.key === selectedCat) || {}).label || selectedCat;
            const rows = (data.categoryInvoices || []).filter((j) => j.category === selectedCat);
            const tot = rows.reduce((s, j) => s + j.amount, 0);
            const dyl = rows.reduce((s, j) => s + j.dylanProduced, 0);
            const col = rows.reduce((s, j) => s + j.coltonProduced, 0);
            const unassigned = rows.filter((j) => !j.soldOwner).reduce((s, j) => s + j.amount, 0);
            return (
              <>
                <div style={S.drawerBackdrop} onClick={() => setSelectedCat(null)} />
                <div style={S.drawer}>
                  <div style={S.drawerHead}>
                    <div>
                      <div style={S.drawerTitle}>{catLabel}</div>
                      <div style={S.drawerSub}>{win.label} · {rows.length} invoice{rows.length === 1 ? "" : "s"}</div>
                    </div>
                    <button style={S.drawerClose} onClick={() => setSelectedCat(null)}>×</button>
                  </div>
                  <div style={S.drawerTotals}>
                    <SmallStat label="Invoiced" value={fmtMoney(tot)} />
                    <SmallStat label="Colton produced" value={fmtMoney(col)} />
                    <SmallStat label="Dylan produced" value={fmtMoney(dyl)} />
                  </div>
                  {unassigned > 0 && (
                    <div style={S.drawerNote}>{fmtMoney(unassigned)} has no salesperson set — counts in the company total but is Unassigned in the Sold lens.</div>
                  )}
                  <div style={S.drawerList}>
                    {rows.map((j, idx) => (
                      <div key={j.invoice_num || idx} style={S.jobRow}>
                        <div style={S.jobHead}>
                          <span style={S.jobClient}>{j.client_name || "—"}</span>
                          <span style={S.jobNum}>#{j.invoice_num || "?"}{j.issued_date ? ` · ${fmtDate(j.issued_date)}` : ""}</span>
                        </div>
                        <div style={S.jobMeta}>
                          <span style={S.jobRev}>{fmtMoney(j.amount)}</span>
                          <span style={S.jobGp}>GP {j.profit_pct == null ? "—" : `${j.profit_pct}%`}</span>
                          {j.status && <span style={S.jobTagStatus}>{j.status}</span>}
                          {j.source === "inferred" && <span style={S.jobTagInf}>category guessed</span>}
                        </div>
                        <div style={S.jobShares}>
                          <span style={S.shareSold}>Sold: {j.salesperson ? j.salesperson.split(/\s+/)[0] : "Unassigned"}</span>
                          {j.coltonProduced > 0 && <span style={S.shareC}>Colton made {fmtMoney(j.coltonProduced)}</span>}
                          {j.dylanProduced > 0 && <span style={S.shareD}>Dylan made {fmtMoney(j.dylanProduced)}</span>}
                        </div>
                      </div>
                    ))}
                    {rows.length === 0 && <div style={S.emptyInline}>No invoices in this category.</div>}
                    {rows.length > 0 && (
                      <div style={S.drawerListTotal}>
                        <span>Total · {rows.length} invoice{rows.length === 1 ? "" : "s"}</span>
                        <span style={{ fontWeight: 800 }}>{fmtMoney(tot)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div style={S.smallStat}>
      <div style={S.smallStatLabel}>{label}</div>
      <div style={S.smallStatValue}>{value}</div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div style={S.stat}>
      <div style={S.statLabel}>{label}</div>
      <div style={S.statValue}>{value}</div>
      {sub && <div style={S.statSub}>{sub}</div>}
    </div>
  );
}

function Mini({ label, value, sub, wide }) {
  return (
    <div style={{ ...S.mini, ...(wide ? { flexBasis: "100%" } : {}) }}>
      <div style={S.miniLabel}>{label}</div>
      <div style={S.miniValue}>{value}</div>
      {sub && <div style={S.miniSub}>{sub}</div>}
    </div>
  );
}

function Badge({ label, pct, count, warnBelow = 0 }) {
  const val = pct == null ? 0 : pct;
  const color = val < warnBelow ? "#f59e0b" : "#a5ce3a";
  return (
    <div style={S.badge}>
      <div style={{ ...S.badgePct, color }}>{pct == null ? "—" : `${pct}%`}</div>
      <div style={S.badgeText}>
        <div style={S.badgeLabel}>{label}</div>
        <div style={S.badgeCount}>{count}</div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: 24, color: "#e8eaf0", fontFamily: "'Roboto', sans-serif", maxWidth: 1400, margin: "0 auto" },
  header: { marginBottom: 16 },
  backLink: { background: "none", border: "none", color: "#a5ce3a", cursor: "pointer", fontSize: 13, fontFamily: "inherit", padding: 0, marginBottom: 6 },
  title: { fontSize: 28, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.02em" },
  subtitle: { fontSize: 13, color: "#8892a4", marginTop: 4, maxWidth: 720 },

  errorBanner: { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" },
  bannerClose: { background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0, marginLeft: 12 },

  controls: { display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" },
  segmented: { display: "inline-flex", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, overflow: "hidden" },
  seg: { padding: "8px 16px", background: "transparent", border: "none", color: "#8892a4", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  segActive: { padding: "8px 16px", background: "#a5ce3a", border: "none", color: "#0d0f12", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  navGroup: { display: "flex", alignItems: "center", gap: 8 },
  navBtn: { width: 32, height: 32, background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#f0f0f0", fontSize: 16, cursor: "pointer", fontFamily: "inherit" },
  rangeLabel: { fontSize: 14, color: "#a5ce3a", fontWeight: 700, minWidth: 190, textAlign: "center" },
  todayBtn: { padding: "6px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#8892a4", fontSize: 12, cursor: "pointer", fontFamily: "inherit" },

  empty: { padding: 40, textAlign: "center", color: "#8892a4" },
  emptyInline: { padding: "12px 4px", color: "#8892a4", fontSize: 13 },

  companyStrip: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 },
  stat: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 14 },
  statLabel: { fontSize: 11, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 800, color: "#f0f0f0" },
  statSub: { fontSize: 11, color: "#8892a4", marginTop: 4 },

  ownerGrid: { display: "grid", gap: 14, marginBottom: 16 },
  ownerCard: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 18 },
  ownerName: { fontSize: 18, fontWeight: 800, color: "#e8eaf0", display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  ownerDot: { width: 10, height: 10, borderRadius: "50%", background: "#a5ce3a", display: "inline-block" },
  sectionLabel: { fontSize: 10, color: "#a5ce3a", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", margin: "14px 0 6px" },
  kpiRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  mini: { flex: "1 1 0", minWidth: 90, background: "#1a1f26", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 12px" },
  miniLabel: { fontSize: 10, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 },
  miniValue: { fontSize: 18, fontWeight: 800, color: "#f0f0f0" },
  miniSub: { fontSize: 10, color: "#5e6675", marginTop: 2 },

  badgeRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginBottom: 16 },
  badge: { display: "flex", alignItems: "center", gap: 12, background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "12px 14px" },
  badgePct: { fontSize: 24, fontWeight: 800, minWidth: 56 },
  badgeText: {},
  badgeLabel: { fontSize: 12, color: "#e8eaf0" },
  badgeCount: { fontSize: 11, color: "#8892a4", marginTop: 2 },

  card: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 16 },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#e8eaf0" },
  cardSub: { fontSize: 12, color: "#8892a4" },

  catRow: { display: "flex", alignItems: "center", gap: 10, padding: "4px 0" },
  catName: { width: 130, fontSize: 12, color: "#c9d2de", flexShrink: 0 },
  catBarTrack: { flex: 1, height: 16, background: "rgba(255,255,255,0.04)", borderRadius: 4, overflow: "hidden" },
  catBarFill: { height: "100%", borderRadius: 4, minWidth: 2 },
  catVal: { width: 130, textAlign: "right", fontSize: 12, color: "#f0f0f0", fontVariantNumeric: "tabular-nums", flexShrink: 0 },
  catPct: { color: "#8892a4", fontSize: 11 },
  catNote: { marginTop: 10, fontSize: 12, color: "#f59e0b", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, padding: "8px 10px" },

  aiBtn: { padding: "8px 14px", background: "#a5ce3a", border: "none", borderRadius: 6, color: "#0d0f12", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  aiHeadline: { fontWeight: 700, color: "#e8eaf0", fontSize: 15, marginBottom: 8 },
  aiSubhead: { fontSize: 11, color: "#a5ce3a", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 10, marginBottom: 4 },
  aiList: { margin: "0 0 4px", paddingLeft: 18 },
  aiWin: { fontSize: 13, color: "#c9d2de", marginBottom: 3 },
  aiFocus: { fontSize: 13, color: "#fcd9a5", marginBottom: 3 },
  aiRec: { fontSize: 13, color: "#c9d2de", marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)" },

  notes: { marginTop: 8 },
  noteLine: { fontSize: 11, color: "#5e6675", marginBottom: 3 },

  // clickable category rows + totals
  catRowBtn: { display: "flex", alignItems: "center", gap: 10, padding: "4px 0", width: "100%", background: "none", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", textAlign: "left" },
  catChevron: { width: 16, textAlign: "center", color: "#5e6675", fontSize: 18, flexShrink: 0 },
  catTotalRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 0 2px", marginTop: 4, borderTop: "1px solid rgba(255,255,255,0.08)" },
  catHint: { marginTop: 10, fontSize: 11, color: "#5e6675", lineHeight: 1.5 },

  // drill-down drawer
  drawerBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000 },
  drawer: { position: "fixed", top: 0, right: 0, height: "100vh", width: "min(440px, 92vw)", background: "#12161c", borderLeft: "1px solid rgba(255,255,255,0.1)", zIndex: 1001, display: "flex", flexDirection: "column", boxShadow: "-8px 0 24px rgba(0,0,0,0.4)" },
  drawerHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "18px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)" },
  drawerTitle: { fontSize: 18, fontWeight: 800, color: "#e8eaf0" },
  drawerSub: { fontSize: 12, color: "#8892a4", marginTop: 3 },
  drawerClose: { background: "none", border: "none", color: "#8892a4", fontSize: 26, lineHeight: 1, cursor: "pointer", padding: 0, fontFamily: "inherit" },
  drawerTotals: { display: "flex", gap: 8, padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" },
  smallStat: { flex: 1, background: "#181d24", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "8px 10px" },
  smallStatLabel: { fontSize: 10, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.04em" },
  smallStatValue: { fontSize: 15, fontWeight: 800, color: "#f0f0f0", marginTop: 2 },
  drawerNote: { margin: "12px 20px 0", fontSize: 11, color: "#f59e0b", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, padding: "8px 10px", lineHeight: 1.5 },
  drawerList: { flex: 1, overflowY: "auto", padding: "12px 20px 24px" },
  drawerListTotal: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 12, borderTop: "2px solid rgba(255,255,255,0.12)", fontSize: 14, fontWeight: 700, color: "#e6ebf2" },
  jobRow: { padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  jobHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 },
  jobClient: { fontSize: 13, fontWeight: 600, color: "#e8eaf0" },
  jobNum: { fontSize: 11, color: "#8892a4", flexShrink: 0 },
  jobMeta: { display: "flex", alignItems: "center", gap: 8, marginTop: 3, flexWrap: "wrap" },
  jobRev: { fontSize: 13, color: "#f0f0f0", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  jobGp: { fontSize: 11, color: "#8892a4" },
  jobTagInf: { fontSize: 10, color: "#a5ce3a", background: "rgba(165,206,58,0.1)", borderRadius: 4, padding: "1px 6px" },
  jobTagWarn: { fontSize: 10, color: "#f59e0b", background: "rgba(245,158,11,0.1)", borderRadius: 4, padding: "1px 6px" },
  jobTagStatus: { fontSize: 10, color: "#8892a4", background: "rgba(255,255,255,0.06)", borderRadius: 4, padding: "1px 6px", textTransform: "capitalize" },
  jobShares: { display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" },
  shareSold: { fontSize: 11, color: "#c9d2de" },
  shareD: { fontSize: 11, color: "#38bdf8" },
  shareC: { fontSize: 11, color: "#a5ce3a" },
};
